// libs
import React, { memo, useCallback, useContext } from "react";
import { HalfCard as DLSHalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// components
import ProductItem from "./widgets/ProductItem";

// styled components
import {
    StyledParent,
    Header,
    Content,
    EmiPlanSection,
    EmiPlanHeader,
    EmiInterestDetails,
    ProductsList,
} from "./styled";

// contexts
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// hooks
import { useEffectOnce } from "@swiggy-private/core-ui";

// helpers
import { ProductDetailsHalfCardInfo } from "@payment-client/interfaces/Checkout";
import { impressionEMIDetailsHC } from "@payment-client/components/show-payments/components/review-bill-details-renderer/helpers/analytics";

interface HalfCardProps {
    isOpen: boolean;
    onClose: () => void;
    halfCardData: ProductDetailsHalfCardInfo;
}

const containerPadding = { left: 16, right: 16, top: 20, bottom: 36 };

const HalfCard: React.FC<HalfCardProps> = ({ isOpen, onClose, halfCardData }) => {
    const { impressionEvent } = useContext(AnalyticsContext);

    useEffectOnce(() => {
        impressionEMIDetailsHC(impressionEvent);
    });

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    if (!halfCardData) {
        return null;
    }

    return (
        <DLSHalfCard
            isOpen={isOpen}
            onClose={handleClose}
            hasCustomPadding
            closeOnOverlayClick
            hideCloseIcon={false}
            variant="v3"
        >
            <StyledParent customPadding={containerPadding}>
                <Header>
                    <Text variant="V2.body1.neutral.bold">{halfCardData.title}</Text>
                </Header>

                <Content>
                    {halfCardData.emiPlanDetails.map((emiPlan) => {
                        const { title, emiInterestDetails, productDetails } = emiPlan;
                        return (
                            <EmiPlanSection key={title}>
                                <EmiPlanHeader>
                                    <Text variant="V2.body2.neutral.bold">{title}</Text>
                                    <EmiInterestDetails variant="V2.overline.neutral.regular" color="text_Low_Emphasis">
                                        {emiInterestDetails}
                                    </EmiInterestDetails>
                                </EmiPlanHeader>

                                <ProductsList>
                                    {productDetails.map((product) => (
                                        <ProductItem key={product.name} product={product} />
                                    ))}
                                </ProductsList>
                            </EmiPlanSection>
                        );
                    })}
                </Content>
            </StyledParent>
        </DLSHalfCard>
    );
};

export default memo(HalfCard);
